import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MeDocumentListQuery,
  MeTimecardListQuery,
  PatchMeProfileBody,
  RequestMeCorrectionBody,
  RequestMeVacationBody,
} from "@evolua/api-client";
import { getApiClient } from "../auth/http.js";
import { useSession } from "../session/SessionContext.js";
import {
  cancelDemoMeVacationRequest,
  createDemoMeDocumentDownload,
  downloadDemoMeDocumentContent,
  downloadDemoMePayslip,
  ackDemoMeAnnouncement,
  getDemoMeAnnouncement,
  getDemoMeBenefit,
  getDemoMeDashboard,
  getDemoMeDocument,
  getDemoMePayslip,
  getDemoMeProfile,
  getDemoMeTimecardBalance,
  getDemoMeVacationBalance,
  getDemoMeVacationHistory,
  listDemoMeAnnouncements,
  listDemoMeBenefits,
  listDemoMeCorrections,
  listDemoMeDocuments,
  listDemoMePayslips,
  listDemoMeTimecards,
  listDemoMeVacationRequests,
  patchDemoMeProfile,
  requestDemoMeCorrection,
  requestDemoMeVacation,
  revealDemoMeField,
} from "./demo-me.js";
import {
  downloadDemoMeDataExport,
  listDemoMeDataExports,
  requestDemoMeDataExport,
} from "./demo-lgpd-export.js";
import { queryKeys } from "./query-keys.js";

/** Portal do colaborador — self-only (FASE-06-T32..T35). */

export function useMeDashboard() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.dashboard(),
    queryFn: () => (isDemoMode ? getDemoMeDashboard() : getApiClient().getMeDashboard()),
  });
}

export function useMeProfile() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.profile(),
    queryFn: () => (isDemoMode ? getDemoMeProfile() : getApiClient().getMeProfile()),
  });
}

export function usePatchMeProfile() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchMeProfileBody) =>
      isDemoMode
        ? Promise.resolve(patchDemoMeProfile(body))
        : getApiClient().patchMeProfile(body),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.me.profile(), profile);
    },
  });
}

export function useRevealMeField() {
  const { isDemoMode } = useSession();
  return useMutation({
    mutationFn: (field: "cpf" | "bankAccount" | "salaryAmount") =>
      isDemoMode
        ? Promise.resolve(revealDemoMeField(field))
        : getApiClient().revealMeField(field),
  });
}

export function useRequestMeCorrection() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RequestMeCorrectionBody) =>
      isDemoMode
        ? Promise.resolve(requestDemoMeCorrection(body))
        : getApiClient().requestMeCorrection(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.corrections() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.dashboard() });
    },
  });
}

export function useMeCorrections() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.corrections(),
    queryFn: () =>
      isDemoMode ? listDemoMeCorrections() : getApiClient().listMeCorrections(),
  });
}

export function useMePayslips() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.payslips(),
    queryFn: () => (isDemoMode ? listDemoMePayslips() : getApiClient().listMePayslips()),
  });
}

export function useMePayslip(id: string | undefined) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.payslip(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error("id obrigatório");
      return isDemoMode ? getDemoMePayslip(id) : getApiClient().getMePayslip(id);
    },
  });
}

export function useDownloadMePayslip() {
  const { isDemoMode } = useSession();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(downloadDemoMePayslip(id))
        : getApiClient().downloadMePayslip(id),
  });
}

export function useMeVacationBalance() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.vacationBalance(),
    queryFn: () =>
      isDemoMode ? getDemoMeVacationBalance() : getApiClient().getMeVacationBalance(),
  });
}

export function useMeVacationHistory() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.vacationHistory(),
    queryFn: () =>
      isDemoMode ? getDemoMeVacationHistory() : getApiClient().getMeVacationHistory(),
  });
}

export function useMeVacationRequests() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.vacationRequests(),
    queryFn: () =>
      isDemoMode ? listDemoMeVacationRequests() : getApiClient().listMeVacationRequests(),
  });
}

export function useRequestMeVacation() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RequestMeVacationBody) =>
      isDemoMode
        ? Promise.resolve(requestDemoMeVacation(body))
        : getApiClient().requestMeVacation(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.vacationRequests() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.dashboard() });
    },
  });
}

export function useCancelMeVacation() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(cancelDemoMeVacationRequest(id))
        : getApiClient().cancelMeVacationRequest(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.vacationRequests() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.dashboard() });
    },
  });
}

export function useMeDataExports() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.dataExports(),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(listDemoMeDataExports())
        : getApiClient().listMeDataExports(),
  });
}

export function useRequestMeDataExport() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      isDemoMode
        ? Promise.resolve(requestDemoMeDataExport())
        : getApiClient().requestMeDataExport(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.dataExports() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.inboxAll() });
    },
  });
}

export function useDownloadMeDataExport() {
  const { isDemoMode } = useSession();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(downloadDemoMeDataExport(id))
        : getApiClient().downloadMeDataExport(id),
  });
}

export function useMeBenefits() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.benefits(),
    queryFn: () => (isDemoMode ? listDemoMeBenefits() : getApiClient().listMeBenefits()),
  });
}

export function useMeBenefit(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.benefit(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(getDemoMeBenefit(id!))
        : getApiClient().getMeBenefit(id!),
  });
}

export function useMeDocuments(filters: MeDocumentListQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.documents(filters),
    queryFn: () =>
      isDemoMode ? listDemoMeDocuments(filters) : getApiClient().listMeDocuments(filters),
  });
}

export function useMeDocument(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.document(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(getDemoMeDocument(id!))
        : getApiClient().getMeDocument(id!),
  });
}

export function useDownloadMeDocument() {
  const { isDemoMode } = useSession();
  return useMutation({
    mutationFn: async (id: string) => {
      const signed = isDemoMode
        ? createDemoMeDocumentDownload(id)
        : await getApiClient().createMeDocumentDownload(id);
      const file = isDemoMode
        ? downloadDemoMeDocumentContent(signed.downloadUrl)
        : await getApiClient().downloadMeDocumentContent(signed.downloadUrl);
      return { ...file, auditId: signed.auditId, expiresAt: signed.expiresAt };
    },
  });
}

export function useMeAnnouncements() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.announcements(),
    queryFn: () =>
      isDemoMode ? listDemoMeAnnouncements() : getApiClient().listMeAnnouncements(),
  });
}

export function useMeAnnouncement(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.announcement(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(getDemoMeAnnouncement(id!))
        : getApiClient().getMeAnnouncement(id!),
  });
}

export function useAckMeAnnouncement() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(ackDemoMeAnnouncement(id))
        : getApiClient().ackMeAnnouncement(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.announcements() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.dashboard() });
    },
  });
}

export function useMeTimecards(filters: MeTimecardListQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.timecards(filters),
    queryFn: () =>
      isDemoMode ? listDemoMeTimecards(filters) : getApiClient().listMeTimecards(filters),
  });
}

export function useMeTimecardBalance() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.timecardBalance(),
    queryFn: () =>
      isDemoMode ? getDemoMeTimecardBalance() : getApiClient().getMeTimecardBalance(),
  });
}
